import { Action, Rule } from 'ability';
import { Administrator } from 'mbs-ui-shared@models';
import { Permission, rulesType } from 'mbs-ui-shared@models/auth-models';

export class PermissionsService {
  public static handlePermissions(user: Administrator): rulesType[] {
    const preparedPermissions = this.preparePermissions(user.Permissions as Permission[]);

    Object.assign(user, { Permissions: preparedPermissions });

    const rules: rulesType[] = Object.keys(user.Permissions)
      .map((key) => {
        const actions: Action = 'read';
        return { actions, subject: key, access: user.Permissions[key] };
      })
      .filter((p) => p.access);

    if (user?.IsProvider) {
      rules.push({ actions: 'read', subject: 'Provider' });
    } else {
      rules.push({ actions: 'read', subject: 'SubAdmin' });
    }

    if (user?.ProviderInfo?.RmmCeEnabled) {
      rules.push({ actions: 'read', subject: 'RmmCeEnabled' });
    } else {
      // remove duplicate permission for RMM CE if not enabled
      const rmmCeEnabledIndex = rules.findIndex((r) => r.subject === 'RmmCeEnabled');

      if (rmmCeEnabledIndex !== -1) {
        rules.splice(rmmCeEnabledIndex, 1);
      }
    }

    if (user?.ProviderInfo?.ManageRmmApiTokens) {
      rules.push({ actions: 'read', subject: 'ManageRmmApiTokens' });
    }

    if (user.IsSuperAdmin) {
      rules.push({ actions: ['create', 'update', 'delete', 'read', 'save'], subject: 'SuperAdmin' });
    }

    if (user.Is2FAEnabled) {
      rules.push({ actions: ['create', 'update', 'delete', 'read', 'save'], subject: '2FAEnabled' });
    }

    rules.push({
      actions: ['create', 'update', 'delete', 'read', 'save'],
      subject: 'Readonly',
      inverted: !user.IsReadOnly
    });

    rules.push(...this.extendedPermission(user, rules));

    return rules;
  }

  private static extendedPermission(user: Administrator, rulesType: rulesType[]): Rule[] {
    const rules: Rule[] = [];
    const ruleTypeSSL = rulesType.find((r) => r.subject === 'CustomCertificatesBinding');
    const crud: Array<Action> = ['create', 'update', 'delete'];

    rules.push(
      { actions: 'read', subject: 'MBS' },
      { actions: 'create', subject: ['Administrator', 'AdministratorInCamelCase'] },
      {
        actions: ['update', 'delete'],
        subject: ['Administrator', 'AdministratorInCamelCase'],
        conditions: { Id: (value) => value !== user.Id }
      },
      {
        actions: 'update',
        subject: ['Administrator', 'AdministratorInCamelCase'],
        conditions: { Id: (value) => value === user.Id },
        fields: ['FirstName', 'LastName', 'Email', 'Password']
      }
      // { actions: ['read', 'update', 'hide'], subject: 'RemoteManagement' }
    );

    if (user?.IsProvider) {
      rules.push({ actions: crud, subject: 'Company' });
      rules.push({ actions: crud, subject: 'CustomCertificatesBinding' });
    } else {
      rules.push({ actions: ['update', 'delete'], subject: 'Company' });
    }

    if (ruleTypeSSL && typeof ruleTypeSSL.subject === 'string') {
      rules.push({
        actions: user.Permissions[ruleTypeSSL.subject],
        subject: 'RealCustomCertificatesBinding'
      });
    }

    return rules;
  }

  private static preparePermissions(permissions: Permission[]): Permissions {
    return permissions?.reduce((acc, permission) => ({ ...acc, [permission.Name.split(':')[1]]: permission.Allow }), {}) as Permissions;
  }
}
